import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Modal from 'components/Modal';
import Loader from 'components/Loader';
import PaginationButtons from 'components/PaginationButtons';
import Icon from 'components/Icon';
import { Colors } from 'modules/theme';
import LocationAlertCard from './LocationAlertCard';

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 280px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eef4f8;
`;

const HeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: #64748b;
  line-height: 1.45;
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  background: #eff6ff;
  color: ${Colors.blue};
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: min(58vh, 520px);
  overflow-y: auto;
  padding-right: 4px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #eef4f8;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 24px;
  color: #64748b;
`;

const EmptyIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: #f0fdf4;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h4`
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  max-width: 320px;
`;

const LocationAlertsModal = ({
  alerts,
  currentPage,
  isVisible,
  loading,
  onClose,
  onPageChange,
  totalPages,
}) => {
  const alertCount = alerts?.length ?? 0;

  return (
    <Modal title="Alerts" onClose={onClose} isVisible={isVisible} size="large">
      <ModalBody>
        {loading ? (
          <Loader size={100} block />
        ) : alertCount > 0 ? (
          <>
            <Header>
              <HeaderCopy>
                <Subtitle>
                  Recent notifications for this location, ordered from newest to oldest.
                </Subtitle>
              </HeaderCopy>
              <CountBadge>
                {alertCount} alert{alertCount !== 1 ? 's' : ''}
              </CountBadge>
            </Header>

            <List>
              {alerts.map(alert => (
                <LocationAlertCard key={alert.id} alert={alert} />
              ))}
            </List>

            {totalPages > 1 && (
              <Footer>
                <PaginationButtons
                  currentPage={currentPage}
                  changePage={onPageChange}
                  totalPages={totalPages}
                />
              </Footer>
            )}
          </>
        ) : (
          <EmptyState>
            <EmptyIcon>
              <Icon name="check-circle" color={Colors.green} fontSize={28} />
            </EmptyIcon>
            <EmptyTitle>No alerts right now</EmptyTitle>
            <EmptyText>
              This location has no active notifications. New alerts will appear here when a device
              triggers one.
            </EmptyText>
          </EmptyState>
        )}
      </ModalBody>
    </Modal>
  );
};

LocationAlertsModal.propTypes = {
  alerts: PropTypes.array,
  currentPage: PropTypes.number,
  isVisible: PropTypes.bool,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  totalPages: PropTypes.number,
};

LocationAlertsModal.defaultProps = {
  alerts: [],
  currentPage: 1,
  isVisible: false,
  loading: false,
  totalPages: 1,
};

export default LocationAlertsModal;
